import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Edit3, Trash2, ExternalLink, CheckCircle, XCircle, Calendar as CalendarIcon } from 'react-feather';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import Calendar from '../../components/shared/ui/Calendar';
import { Asset } from '../../types';
import { PlatformKey } from '../../utils/platformConfig';
import { ResponsiveEmbed } from '../../components/shared/ui/ResponsiveEmbed';
import { EmbedWithPlaceholder } from '../../components/shared/ui/EmbedWithPlaceholder';
import { PLATFORM_CONFIGS } from '../../utils/platformConfig';
import { formatYMD, parseLocalDate } from '../../utils/datePlain';
import { createPortal } from 'react-dom';
import { extractCanonicalUrl, normalizeUrl } from '../../utils/embedDetect';
import { AutoSizingViewport } from '../../components/shared/AutoSizingViewport';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { logger } from '../../utils/logger';

export interface AssetCardProps {
  asset: Asset;
  onUpdate: (asset: Asset) => Promise<void> | void;
  onDelete: (id: string) => void;
  isInternalUser?: boolean;
  labelClassName?: string;
  deferLoad?: boolean;
}

const AssetCard: React.FC<AssetCardProps> = React.memo(({
  asset,
  onUpdate,
  onDelete,
  isInternalUser = true,
  labelClassName = "text-xs theme-text-muted uppercase tracking-wider"
}) => {
  const safeAsset = { ...asset, embedCodes: asset.embedCodes || [] };

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const canEdit = isInternalUser;
  const platform = asset.platform;

  const dateInputRef = useRef<HTMLSpanElement>(null);

  // Draft state
  const [draftTitle, setDraftTitle] = useState(asset.title);
  const [draftDate, setDraftDate] = useState(asset.postedAt);

  useEffect(() => {
    if (isEditing) {
      setDraftTitle(asset.title);
      setDraftDate(asset.postedAt);
    }
  }, [isEditing, asset.title, asset.postedAt]);

  // Floating UI (calendar)
  const { x, y, refs, strategy } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (refs.floating.current && refs.floating.current.contains(target)) return;
      if (dateInputRef.current && dateInputRef.current.contains(target)) return;
      setShowCalendar(false);
    };
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showCalendar, refs.floating]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      const updatedAsset = { ...asset, title: draftTitle, postedAt: draftDate };
      await onUpdate(updatedAsset);
      setIsEditing(false);
      setShowCalendar(false);
    } finally {
      setIsLoading(false);
    }
  }, [asset, draftTitle, draftDate, onUpdate]);

  const handleCancel = useCallback(() => {
    setDraftTitle(asset.title);
    setDraftDate(asset.postedAt);
    setIsEditing(false);
    setShowCalendar(false);
  }, [asset.title, asset.postedAt]);

  const handleDelete = useCallback(() => {
    onDelete(asset.id);
    setShowDeleteConfirm(false);
  }, [asset.id, onDelete]);

  useEffect(() => {
    if (!showDeleteConfirm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); handleDelete(); }
      else if (e.key === 'Escape') { e.preventDefault(); setShowDeleteConfirm(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showDeleteConfirm, handleDelete]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY });
  }, []);

  const handleToggleVisibility = useCallback(async () => {
    try {
      const newApproved = asset.approved === false ? true : false;
      await onUpdate({ ...asset, approved: newApproved });
    } catch (error) {
      // Update may fail due to network issues or permissions
      if (import.meta.env.DEV) {
        logger.error('Failed to toggle asset visibility', { error, assetId: asset.id }, 'AssetCard');
      }
    }
  }, [asset, onUpdate]);

  const needsMediaScrollbar = useMemo(() => {
    const plat = platform?.toLowerCase();
    return plat === 'twitter' || plat === 'tiktok' || plat === 'instagram';
  }, [platform]);

  // --- mapping/platform utils (unchanged)
  const mapToPlatformKey = useCallback((platform: string): PlatformKey | null => {
    const platformLower = platform.toLowerCase();
    if (Object.keys(PLATFORM_CONFIGS).includes(platformLower)) {
      return platformLower as PlatformKey;
    }
    const platformMap: Record<string, PlatformKey> = {
      'youtube-shorts': 'youtube-shorts',
      'instagram-reel': 'instagram',
      'fb': 'facebook',
      'fb-post': 'facebook',
      'fb-video': 'facebook',
      'x': 'twitter',
      'linkedin-post': 'linkedin'
    };
    if (platformMap[platformLower]) return platformMap[platformLower];
    for (const [key] of Object.entries(PLATFORM_CONFIGS)) {
      if (platformLower.includes(key) || key.includes(platformLower)) return key as PlatformKey;
    }
    return null;
  }, []);

  // Scroll-triggered fade-in animation (Phase 3)
  const scrollAnim = useScrollAnimation({
    threshold: 0.1,
    rootMargin: '50px',
    triggerOnce: true,
    staggerDelay: 0
  });

  return (
    <>
      <div
        ref={scrollAnim.ref}
        style={scrollAnim.style}
        className="group focus:outline-none focus:ring-2 focus:ring-[var(--ring)] transition-all duration-300 rounded-xl elevation-card asset-card-masonry h-fit flex flex-col overflow-hidden"
      >
        <div
          className="w-full relative"
          style={{ minHeight: 0 }}
          tabIndex={0}
          onContextMenu={handleContextMenu}
        >
          {/* Loading Overlay */}
          {isLoading && platform !== 'link' && (
            <div className="absolute inset-0 theme-surface/95 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-blue-600 border-t-transparent"></div>
                <p className="text-sm theme-text-muted font-medium">Loading content...</p>
              </div>
            </div>
          )}

          {/* MEDIA REGION — fixed aspect box so iframes have real height */}
          <div
            className={`${needsMediaScrollbar ? 'asset-media-scroll' : ''}`}
          >
            {/* 16:9 responsive box with a sensible floor for short previews */}
            <div className="relative w-full aspect-video min-h-[220px]">
              {/* Absolute fill so children using 100% height have pixels to work with */}
              <div className="absolute inset-0 w-full h-full">
                {platform === 'link' && asset.originalUrl ? (
                  // Use EmbedWithPlaceholder for link previews to match 3D carousel style
                  // This provides the nice favicon card with domain, path, and Open button
                  <EmbedWithPlaceholder
                    platform="link"
                    input={asset.originalUrl}
                    url={asset.originalUrl}
                    className="w-full h-full"
                    deferLoad={false}
                    aspect={1.777}
                  />
                ) : (
                  (() => {
                    const displayPlatform = (platform || '').toLowerCase();
                    const platformKey = mapToPlatformKey(platform || '');
                    const firstEmbed = safeAsset.embedCodes[0] || '';
                    if (!platformKey) {
                      return (
                        <div className="w-full h-full p-4 theme-muted border theme-border rounded flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-sm theme-text-muted mb-2">Unsupported platform: {platform}</p>
                            <p className="text-xs theme-text-muted">Input: {firstEmbed.substring(0, 100)}...</p>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <AutoSizingViewport platform={displayPlatform}>
                        <div
                          className={`asset-embed w-full h-full ${
                            platform === 'tiktok' || platform === 'twitter' ? 'asset-embed-scroll' :
                            platform === 'instagram' ? 'instagram-scrollable' : ''
                          }`}
                          data-platform={displayPlatform}
                        >
                          <ResponsiveEmbed
                            key={`${asset.id}-embed-0`}
                            platform={platformKey}
                            input={
                              platformKey === "tiktok"
                                ? (firstEmbed || asset.originalUrl || "")
                                : platformKey === "twitter"
                                  ? (firstEmbed || asset.originalUrl || "")
                                  : (asset.originalUrl || firstEmbed || "")
                            }
                            className="w-full h-full"
                          />
                        </div>
                      </AutoSizingViewport>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-4 py-3 border-t theme-border theme-surface flex-shrink-0 group-hover:bg-opacity-90 transition-colors">
          <div className="space-y-2">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-2">
              <a
                href={(() => {
                  function getOriginalUrl(a: Asset): string | null {
                    if (a.originalUrl) {
                      try { const n = normalizeUrl(a.originalUrl); new URL(n); return n; } catch {}
                    }
                    const first = a.embedCodes?.[0] || '';
                    if (first) {
                      const extracted = extractCanonicalUrl(first);
                      if (extracted) {
                        try { const n = normalizeUrl(extracted); new URL(n); return n; } catch {}
                      }
                    }
                    return null;
                  }
                  return getOriginalUrl(asset);
                })() || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.stopPropagation(); if (!(() => {
                  function getOriginalUrl(a: Asset): string | null {
                    if (a.originalUrl) {
                      try { const n = normalizeUrl(a.originalUrl); new URL(n); return n; } catch {}
                    }
                    const first = a.embedCodes?.[0] || '';
                    if (first) {
                      const extracted = extractCanonicalUrl(first);
                      if (extracted) {
                        try { const n = normalizeUrl(extracted); new URL(n); return n; } catch {}
                      }
                    }
                    return null;
                  }
                  return getOriginalUrl(asset);
                })()) e.preventDefault(); }}
                className="clickable-asset-title flex items-center gap-2 text-base font-semibold theme-text hover:text-[var(--ring)] dark:hover:text-blue-400 transition-all duration-300 line-clamp-2 flex-1 min-w-0 cursor-pointer hover:underline"
              >
                <span className="truncate">{asset.title || 'Untitled Asset'}</span>
                <ExternalLink className="w-4 h-4 flex-shrink-0 opacity-60 group-hover:opacity-100" />
              </a>
              {asset.approved === false && (
                <span className="flex-shrink-0 inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-yellow-400/20 text-yellow-600 dark:text-yellow-400 border border-yellow-400/30">
                  Hidden
                </span>
              )}
            </div>
            
            {/* Metadata Row */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 theme-text-muted">
                <CalendarIcon className="w-3.5 h-3.5" />
                <span>{asset.postedAt ? (parseLocalDate(asset.postedAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || 'Invalid date') : 'No date'}</span>
              </div>
              
              {canEdit && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={handleToggleVisibility}
                    className="clickable-icon-btn p-1.5 rounded-md theme-text-muted hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200 cursor-pointer hover:scale-110"
                    title={asset.approved === false ? "Make Visible" : "Hide from Viewers"}
                    aria-label={asset.approved === false ? "Make Visible" : "Hide from Viewers"}
                  >
                    {asset.approved === false ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="clickable-icon-btn p-1.5 rounded-md theme-text-muted hover:text-[var(--accent-primary)] hover:bg-[color-mix(in_srgb,var(--accent-primary) 12%, transparent)] transition-all duration-200 cursor-pointer hover:scale-110"
                    title="Edit Asset"
                    aria-label="Edit Asset"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="clickable-icon-btn p-1.5 rounded-md theme-text-muted hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 cursor-pointer hover:scale-110"
                    title="Delete Asset"
                    aria-label="Delete Asset"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* EDIT MODAL */}
        {isEditing && createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center z-[9999]"
            style={{ backgroundColor: 'var(--modal-backdrop)', isolation: 'isolate' }}
            onClick={handleCancel}
          >
            <div
              className="theme-surface rounded-lg p-6 max-w-md w-full mx-4 shadow-xl relative z-[10000]"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium theme-text mb-4">Edit Asset</h3>
              <div className="space-y-4">
                <div>
                  <label className={`block ${labelClassName} mb-2 text-sm font-medium`}>Title</label>
                  <input
                    type="text"
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    className="w-full px-3 py-2 text-base border border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-800 text-white"
                    placeholder="Enter title..."
                  />
                </div>
                <div>
                  <label className={`block ${labelClassName} mb-2 text-sm font-medium`}>Date Posted</label>
                  <div className="flex items-center space-x-2">
                    <span
                      ref={(el) => { refs.setReference(el); dateInputRef.current = el; }}
                      className="flex-1 px-3 py-2 text-base border border-gray-600 rounded-md cursor-pointer hover:border-blue-500 transition-colors text-white bg-gray-800"
                      onClick={() => setShowCalendar(true)}
                    >
                      {draftDate ? (parseLocalDate(draftDate)?.toLocaleDateString() || 'Invalid date') : 'Select date'}
                    </span>
                  </div>
                  {showCalendar && createPortal(
                    <div
                      ref={refs.setFloating}
                      data-floating-ui-portal
                      style={{
                        position: strategy,
                        top: y ?? 0,
                        left: x ?? 0,
                        zIndex: 11000,
                        borderRadius: '0.5rem',
                        boxShadow: 'var(--shadow-strong)',
                        backdropFilter: 'none',
                        WebkitBackdropFilter: 'none',
                        opacity: 1,
                        isolation: 'isolate'
                      }}
                      className="p-2 border border-[var(--border)] bg-[var(--bg-elev)] text-[var(--text)]"
                    >
                      <Calendar
                        value={draftDate ? (parseLocalDate(draftDate) ?? new Date()) : new Date()}
                        onChange={date => { setDraftDate(formatYMD(date)); }}
                        onClose={() => setShowCalendar(false)}
                        showMonthYearDropdown={true}
                      />
                    </div>,
                    document.body
                  )}
                </div>
                <div className="flex justify-end space-x-3 pt-3">
                  <button onClick={handleCancel} className="px-5 py-2 text-base font-medium theme-text-muted theme-surface border theme-border rounded-md hover:theme-muted transition-colors">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={isLoading} className="px-5 py-2 text-base font-medium text-white bg-[var(--accent-primary)] rounded-md hover:bg-[color-mix(in_srgb,var(--accent-primary) 85%, #ffffff 15%)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* DELETE MODAL */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'var(--modal-backdrop)' }} onClick={() => setShowDeleteConfirm(false)}>
          <div className="theme-surface bg-[var(--bg-elev)] rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-medium theme-text mb-4">Delete Asset</h3>
            <p className="text-sm theme-text-muted mb-6">Are you sure you want to delete this asset? This action cannot be undone.</p>
            <p className="text-xs theme-text-muted mb-4">
              Press <kbd className="px-1.5 py-0.5 theme-muted border theme-border rounded text-xs">Enter</kbd> to confirm or <kbd className="px-1.5 py-0.5 theme-muted border theme-border rounded text-xs">ESC</kbd> to cancel
            </p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-sm font-medium theme-text-muted theme-surface border theme-border rounded-md hover:theme-muted transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTEXT MENU */}
      {contextMenu && (
        <div className="fixed theme-surface border theme-border rounded-md shadow-lg py-1 z-50" style={{ left: contextMenu.x, top: contextMenu.y }}>
          <button
            onClick={() => { setIsEditing(!isEditing); setContextMenu(null); }}
            className="w-full px-4 py-2 text-left text-sm theme-text-muted hover:theme-muted transition-colors"
          >
            {isEditing ? 'Stop Editing' : 'Edit Asset'}
          </button>
          {canEdit && (
            <button
              onClick={() => { setShowDeleteConfirm(true); setContextMenu(null); }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:theme-muted transition-colors"
            >
              Delete Asset
            </button>
          )}
        </div>
      )}
    </>
  );
});

export default AssetCard;